import type { PillarId } from '@dwa/shared';
import { defaultSelectionFor, questionsForSelection, termsFor } from '@dwa/shared';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from '../components/ui';
import { useFramework } from '../hooks/useFramework';
import { useAssessmentStore } from '../state/assessmentStore';
import { NotFound } from './NotFound';

export function Select() {
  const navigate = useNavigate();
  const { focus } = useParams<{ focus: string }>();
  const fw = useFramework(focus);
  const storedPillars = useAssessmentStore((s) => s.pillars);
  const setPillars = useAssessmentStore((s) => s.setPillars);
  const setFocus = useAssessmentStore((s) => s.setFocus);

  // null until seeded (from a resumed selection or the framework's core defaults).
  const [selected, setSelected] = useState<PillarId[] | null>(null);
  const seeded = useRef(false);

  // Entering /:focus selects that WAR (clearing any prior WAR's selection).
  useEffect(() => {
    if (focus) setFocus(focus);
    seeded.current = false;
  }, [focus, setFocus]);

  // Once the framework loads, seed the toggles: resume stored pillars, else core.
  useEffect(() => {
    if (!fw.data || seeded.current) return;
    seeded.current = true;
    setSelected(storedPillars.length > 0 ? storedPillars : defaultSelectionFor(fw.data));
  }, [fw.data, storedPillars]);

  if (fw.isError) {
    return <NotFound title="Review not found" message="That review doesn’t exist or isn’t available yet." />;
  }
  if (fw.isPending || !fw.data || selected === null) return <Spinner label="Loading the review…" />;
  const framework = fw.data;
  const terms = termsFor(framework);
  const sel = selected;

  const toggle = (id: PillarId) =>
    setSelected((cur) => ((cur ?? []).includes(id) ? (cur ?? []).filter((p) => p !== id) : [...(cur ?? []), id]));

  const questionCount = questionsForSelection({ pillars: sel }, framework).length;

  const start = () => {
    setPillars(sel);
    navigate('/review');
  };

  const renderPillar = (p: (typeof framework.pillars)[number]) => {
    const on = sel.includes(p.id);
    const count = framework.questions.filter((q) => q.pillarId === p.id).length;
    return (
      <button
        key={p.id}
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => toggle(p.id)}
        className={`rounded-xl border p-5 text-left transition ${
          on ? 'border-climb-cyan bg-climb-cyan/10' : 'border-climb-line-dark bg-climb-surface hover:border-climb-teal-soft'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-medium text-climb-paper">{p.name}</h3>
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs ${
              on ? 'border-climb-cyan bg-climb-cyan text-climb-teal' : 'border-climb-teal-soft/50 text-transparent'
            }`}
            aria-hidden
          >
            ✓
          </span>
        </div>
        <p className="mt-1 text-sm text-climb-teal-soft">{p.description}</p>
        <p className="mt-2 text-xs text-climb-teal-soft/70">{count} questions</p>
      </button>
    );
  };

  const core = framework.pillars.filter((p) => p.category === 'Core');
  const lenses = framework.pillars.filter((p) => p.category === 'Lens');

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-climb-cyan">{framework.title}</div>
        <h1 className="mt-1 text-2xl font-medium text-climb-paper">Choose your {terms.unitPlural}</h1>
        <p className="mt-2 text-climb-teal-soft">
          {terms.coreGroupLabel} are selected by default. Toggle off any that don’t apply, and add the{' '}
          {terms.lensGroupLabel.toLowerCase()} that fit your deployment.
        </p>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-climb-cyan">{terms.coreGroupLabel}</h2>
        <div className="grid gap-3 sm:grid-cols-2">{core.map(renderPillar)}</div>
      </section>

      {lenses.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-climb-cyan">{terms.lensGroupLabel}</h2>
          <div className="grid gap-3 sm:grid-cols-2">{lenses.map(renderPillar)}</div>
        </section>
      )}

      <div className="flex items-center justify-between border-t border-climb-line-dark pt-4">
        <span className="text-sm text-climb-teal-soft">
          {sel.length} {sel.length === 1 ? terms.unit : terms.unitPlural} · {questionCount} questions
        </span>
        <Button onClick={start} disabled={sel.length === 0}>
          Start the review
        </Button>
      </div>
    </div>
  );
}
